import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { asyncConnect } from 'redux-connect';
import { replace } from 'react-router-redux';

import { setBusy } from 'modules/Common/reducers/pageTranstition';
import { logout } from 'modules/Common/reducers/skysso';
import { resetSubscriptions } from 'modules/Common/reducers/userService';
import { setStatus } from 'redux/server';

import { watchMediaQuery } from 'utils/mediaQueryHelpers';
import { preventLoadData } from 'layouts';

import config from 'config';
const {isProduction} = config; // direct import doesn't work

export function standardAsyncConnect(dispatches) {
  return layoutAsyncConnect("", dispatches);
}

export function layoutAsyncConnect(componentName, dispatches) {

  // seed the collection with something that will simply set the transtion to busy
  const asynced = [{
    promise: ({store: {dispatch, getState}}) => {
      if (!getState().common.pageTranstition.busy) { // ensure no duplicated dispatches
        dispatch(setBusy(true));
      }
    }
  }];

  // Require this for error handling and status codes on the server.
  const connections = [];
  const errorConnections = [];
  const refresh = [];

  dispatches.forEach(element => {
    const {
      // A function which returns a boolean,
      // used to determine if the dispatch method should be executed.
      preventResolve = () => false,

      // The function to dispatch.
      dispatch = () => {},

      // The part of the redux state which is of interest
      state,

      // The part of the redux state which reports errors for this component
      errorState,

      // If set to a number greater than zero,
      // the dispatch function will automatically be called repeatedly at the given interval
      refreshInterval = 0,

      // If set to true then current running timeouts will be preserved if the component is
      // NOT unmounted i.e switch between tabs on a screen, the route will change but the component
      // will not unmount.
      preserveRefresh = false,

      // This function will be called AFTER the dispatch method has been resolved.
      // Useful for when an API call relies on another and it doesnt make sense to make another component.
      // If a third API call is needed, theres probably something wrong and should be redesigned.
      then = () => {},

      // This function will be called when the async connect wrapper is unmounted.
      // If preserveRefresh is set to true, it is likely this method will need an implementation.
      // This method is ideal for resetting state.
      reset,

      // This can be set to true to ignore errors when they occur. If the component handles errors
      // with the renderError method that will be called, otherwise the default render will be used.
      handleError = true,

      // This flag should only be set ONCE for the entire API call heirarchy when a page is loaded.
      // If this flag is set to true, it will allow this component to push status codes to the
      // express server. If multiple of this flag are set behaviour is unpredictable and the last
      // call to complete will probably set the state.
      reportStatus = false
    } = element;

    const dispatchFunc = dispatch;
    const thenFunc = then;

    asynced.push({
      promise: ({store: {dispatch, getState}, location, location: {pathname}, routes, params}) => {
        const promiseState = getState();
        // Define any helper stuff here and include them in the promise destructuring below.
        const {
          common: {
            auth,
            auth: {
              loggedIn = false
            } = {}
          } = {}
        } = promiseState;

        if (preventResolve({state: promiseState, loggedIn, params}) || (componentName && preventLoadData(componentName, {state: promiseState, location, routes, params}))) {
          return Promise.resolve();
        } else {
          return dispatch(dispatchFunc({dispatch, getState, state: promiseState, pathname, params, routes, auth}))
            .then(result => thenFunc({dispatch, getState, state: promiseState, pathname, params, routes, auth, result}))
            .catch(caught => {
              const { body = {}, status = 500, error } = caught;

              if (!isProduction) {
                if (error) {
                  console.error("Error in Page Transition:", body.message, error.message); // eslint-disable-line no-console
                }
                else {
                  console.error("Error in Page Transition;", caught); // eslint-disable-line no-console
                }
              }

              // If an API call reports the user isn't authorised, log them out, and prevent error redirection
              let userAuthorised;
              if (status === 401 || status === 403) {
                dispatch(logout());
                dispatch(resetSubscriptions());
                userAuthorised = false;
              }
              else {
                userAuthorised = true;
              }

              // this component reports it is capable of handling its own errors.
              if (handleError) {
                // Make sure we can get a message to the server on initial render.
                // The client app will never use this state, only the server. Only makes
                // sense if we are going to handle the error.
                if (reportStatus) {
                  dispatch(setStatus(status, error.message));
                }
              }

              // 404s have their own error page
              else if (status === 404) {
                dispatch(replace(`/error/404${promiseState.routing.locationBeforeTransitions.pathname}`));
              }

              // if we've got this far, default to reporting a 500
              else if (userAuthorised) {
                dispatch(replace(`/error/500/from${promiseState.routing.locationBeforeTransitions.pathname}`));
              }
            });
        }
      }
    });

    if (refreshInterval) { // non-zero or a function
      refresh.push({dispatch, interval: refreshInterval, preserveRefresh, reset, handleError});
    }

    if (state) {
      connections.push(state);
    }

    if (errorState) {
      errorConnections.push(errorState);
    }
  });

  return Decorated => {
    class AsyncConnectWrapper extends Decorated {

      static contextTypes = {
        store: PropTypes.object.isRequired
      }

      constructor(...args) {
        super(...args);

        this.activeTimeouts = [];
        this.mounted = false;
        this.unwatchTablet = () => {}; // initial noop

        const asyncError = !!this.props.asyncError;
        this.state = {
          ...this.state,
          asyncError
        }
      }

      componentDidMount(...args) {
        if (super.componentDidMount) {
          super.componentDidMount(...args);
        }

        this.unwatchTablet = watchMediaQuery("tablet", () => {
          refresh.forEach(({dispatch}) => {
            // No need to have a callback here as its a single time action.
            this.dispatchWithCallback(dispatch, () => {});
          });
        });

        refresh.forEach(({dispatch, interval}, index) => {
          this.activeTimeouts[index] = this.timeoutCallback(dispatch, interval, index);
        });

        // Set mounted AFTER media query watcher
        this.mounted = true;
      }

      timeoutCallback(dispatch, interval, index) {
        let delay;

        if (typeof interval == 'function') {
          const state = this.context.store.getState();
          const params = this.props.params;

          delay = interval({state, params});
        }
        else {
          delay = interval;
        }

        if (delay) {
          return setTimeout(() => {

            // The callback should over write the timeout reference with a new one.
            this.dispatchWithCallback(dispatch, () => {
              this.activeTimeouts[index] = this.timeoutCallback(dispatch, interval, index);
            });

          }, delay);
        }
      }

      dispatchWithCallback(dispatch, callback) {
        if (this.mounted) {
          const getState = this.context.store.getState;

          const {
            location: {
              pathname = ""
            } = {},
            params = {},
            dispatch: dispatcher,
            routes
          } = this.props;

          const promiseState = getState();

          const {
            common: {
              auth
            }
          } = promiseState;

          // We do not know how the actual function operates to determine
          // if it should be executed or not, however if the component is hidden
          // we don't want to load its data. Check to see if the area of this component
          // meets visiblity criteria.
          const domElement = ReactDOM.findDOMNode(this);
          if (domElement) {
            const rect = domElement.getBoundingClientRect();

            const width = rect.right - rect.left;
            const height = rect.bottom - rect.top;
            const area = width * height;
            // Display threshold is that the component needs to be at least 1px wide or 1px in height.
            const isDisplayed = area > Math.min(height, width) ? true : false

            if (isDisplayed) {
              dispatcher(dispatch({dispatch, getState, state: promiseState, pathname, params, routes, auth}))
                .then(() => callback())
                .catch(e => {
                  if (!isProduction) {
                    console.error("Error in Live Update", e); // eslint-disable-line no-console
                  }
                  this.componentError(e);
                  callback();
                });
            } else {
              callback();
            }
          }
        }
      }

      clearTimeouts() {
        this.activeTimeouts.forEach(timeout => {
          clearTimeout(timeout);
        });
      }

      componentWillUnmount(...args) {
        if (super.componentWillUnmount) {
          super.componentWillUnmount(...args);
        }
        this.mounted = false;
        this.unwatchTablet();
        this.clearTimeouts();
        refresh.forEach(({ reset }) => {
          if (reset) {
            this.props.dispatch(reset());
          }
        });
      }

      componentWillReceiveProps(nextProps) {
        if (super.componentWillReceiveProps) {
          super.componentWillReceiveProps(nextProps);
        }
        if (this.props.location.pathname !== nextProps.location.pathname) {
          refresh.forEach(({dispatch, interval, preserveRefresh}, index) => {
            if (!preserveRefresh) {
              clearTimeout(this.activeTimeouts[index]);
              this.activeTimeouts[index] = this.timeoutCallback(dispatch, interval, index);
            }
          });
        }
      }

      componentError(error) {
        if (refresh.some(({ handleError }) => handleError)) {
          this.setState({
            asyncError: true,
            error
          });
        }
      }

      render() {
        if (this.state.asyncError && typeof super.renderError === "function") {
          return super.renderError();
        } else {
          try {
            return super.render();
          } catch(e) {
            return super.renderError(e);
          }
        }
      }
    }

    return asyncConnect(asynced, (...args) => evaluateConnections(connections, errorConnections, args))(AsyncConnectWrapper);
  }
}

function evaluateConnections(connections, errorConnections, args) {
  let states = {};
  connections.forEach(connection => {
    states = {...states, ...connection(...args)};
  });
  errorConnections.forEach(connection => {
    states = {...states, ...{ asyncError: connection(...args) }};
  });
  return states;
}



// WEBPACK FOOTER //
// ./utils/asyncConnectWrapper.js